import { Component, OnInit, Input, OnDestroy, OnChanges } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ProjectService } from '../../../../../shared/services/project.service';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { UserService } from '../../../../../shared/services/user.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { CompanyService } from '../../../../../shared/services/company.service';
import { forkJoin, of, Subject } from 'rxjs';
import { ProposalInvitationService } from '../../../../../shared/services/proposal-invitation.service';
import { AuthService } from '../../../../../shared/services/auth.service';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';
import { OverlayService } from '../../../../../shared/services/overlayService';
import { PermissionService } from '../../../../../shared/services/permissions.service';
import { SimpleUser } from '../../../../../shared/interfaces';
import { WithPendingGuard } from '../../../../../shared/decorators';
import { ActivationStatus } from '../../../../../login/models/request.invitation.model';

@Component({
  selector: 'mtm-new-user-project',
  templateUrl: './new-user-project.component.html',
  styleUrl: './new-user-project.component.scss',
  standalone: false,
})
export class NewUserProjectComponent implements OnInit, OnDestroy, OnChanges {
  @Input() users: any[] = [];
  @Input() project: any = null;

  addUserForm: UntypedFormGroup;
  submitted: boolean = false;
  projectId: string = null;
  ngUnsubscribe = new Subject();
  companyUsers: any = [];
  invitedUsers: any = [];
  authUser: any = null;
  isPending: boolean = false;

  constructor(
    public userService: UserService,
    private readonly authService: AuthService,
    public readonly service: ProjectService,
    public readonly activeModal: NgbActiveModal,
    private readonly fb: UntypedFormBuilder,
    private readonly translatePipe: TranslatePipe,
    private readonly overlayService: OverlayService,
    private readonly permissionService: PermissionService,
    private readonly notificationService: NotificationService,
    private readonly proposalInvitationService: ProposalInvitationService,
    private readonly companyService: CompanyService
  ) {}

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  ngOnInit() {
    this.loadFormGroup();
    this.init();
  }

  ngOnChanges() {
    this.init();
  }

  private init() {
    this.authUser = this.authService.getAuthUser();

    this.companyService
      .getCompanyUsers()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        this.companyUsers = res;
        this.loadFormGroup();
      });
  }

  private loadFormGroup() {
    this.addUserForm = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
    });
  }

  @WithPendingGuard
  onSubmit(): void {
    const index = this.users.findIndex((k) => k.username == this.addUserForm.value.username);
    if (index > -1) {
      this.showInfo(null, null, 'There is user in the user list');
      return;
    }

    if (this.addUserForm.valid) {
      this.submitted = true;

      if (this.project.projectType === 'SUBSCRIPTION') {
        this.inviteUser(this.addUserForm.value.username);
      } else {
        this.inviteForMarketplaceAdminProject();
      }
    }
  }

  //for marketplace/admin project
  private inviteForMarketplaceAdminProject() {
    if (this.permissionService.isPlatformAdmin()) {
      this.inviteUser(this.addUserForm.value.username);
      return;
    }

    if (this.authUser.company.id == this.project.company.id) {
      this.inviteUser(this.addUserForm.value.username);
      return;
    }

    //if there is accepted proposal & invited must be from production company that wins the pitch
    forkJoin([
      this.userService.getSingleUserInfo(this.addUserForm.value.username).pipe(
        takeUntil(this.ngUnsubscribe),
        catchError((err) => {
          return of(err);
        })
      ),
      this.proposalInvitationService.getProposalByProjectId(this.service.project.id).pipe(
        takeUntil(this.ngUnsubscribe),
        map((proposals: any) => proposals.filter((proposal: any) => proposal.status === 'ACCEPTED'))
      ),
    ]).subscribe((res: any) => {
      const user = res[0];
      if (user.errorCode) {
        this.inviteUser(this.addUserForm.value.username);
        return;
      }

      const proposals = res[1];
      if (!proposals.length) {
        this.overlayService.showError(this.translatePipe.transform('project_addUserError_noAcceptedProposal'), 'Error');
      } else {
        const proposalsFromInvitedUserCompany = proposals.filter(
          (proposal) => user.companyId === proposal.prodCompanyId
        );
        if (!proposalsFromInvitedUserCompany.length) {
          this.overlayService.showError(
            this.translatePipe.transform('project_addUserError_noAcceptedProposal'),
            'Error'
          );
        } else {
          this.inviteUser(this.addUserForm.value.username);
        }
      }
    });
  }

  // User/role assignment moved to rbac-service; the retired setRoles endpoint is gone, so the
  // modal resolves from the local user cache and closes without a write.
  inviteUser(email: any): void {
    const tempUser = {
      email: email,
      username: email,
      activationStatus: ActivationStatus.PENDING_INPROGRESS,
    };
    const existingUser: SimpleUser | undefined = this.userService.getCachedUserByUsername(email);
    this.closeModal(existingUser ? { user: existingUser } : { invited: true, user: tempUser });
  }

  closeModal(model?) {
    this.activeModal.close(model);
  }

  private showInfo(callback, model, message = null) {
    this.notificationService
      .open({
        title: 'User Invitation',
        description: message ? message : this.translatePipe.transform('userRoleError'),
        confirmBtn: 'Accept',
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res) => {
          if (callback) callback(model);
        },
        (err) => {
          if (callback) callback(model);
        }
      );
  }

  isValid(field: string): boolean {
    if (field == 'username')
      this.addUserForm.patchValue({
        username: this.addUserForm.get(field).value.trim(),
      });
    return this.addUserForm.get(field).invalid && this.submitted;
  }

  selectedInvitersChange(e) {
    const username = e.value.length && !e.invalid ? e.value[0] : '';
    this.addUserForm.setValue({ username });
  }
}
